import React, { useState, useEffect } from "react";
import Modal from "./Modal";
import authService from "../../services/authService";
import { formatRoleName } from "../../utils/stringUtils";

const InvitationLinkItem = ({ invitation }) => {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);

  const roleLabel = formatRoleName(invitation.roleName);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(invitation.invitationUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy invitation link", error);
    }
  };

  const isExpired =
    !invitation.isMultiUse && invitation.expiresAt
      ? new Date(invitation.expiresAt) < new Date()
      : false;

  const daysUntilExpiry =
    !invitation.isMultiUse && invitation.expiresAt
      ? Math.ceil(
          (new Date(invitation.expiresAt) - new Date()) /
            (1000 * 60 * 60 * 24),
        )
      : null;

  return (
    <div className="bg-white border border-pale-dawn rounded-xl p-4">
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          <div
            className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
              invitation.isMultiUse
                ? "bg-amber-100 text-amber-600"
                : "bg-blue-50 text-blue-500"
            }`}
          >
            <i
              className={`fas ${
                invitation.isMultiUse ? "fa-link" : "fa-envelope-open"
              }`}
            ></i>
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0 mb-1 flex-wrap">
              <span
                className="font-bold text-grey-dusk truncate min-w-0"
                title={roleLabel}
              >
                {roleLabel}
              </span>

              {invitation.professionType && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium whitespace-nowrap flex-shrink-0">
                  {invitation.professionType}
                </span>
              )}

              {invitation.isMultiUse && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 font-bold border border-amber-200 whitespace-nowrap flex-shrink-0">
                  Persistent
                </span>
              )}
            </div>

            <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
              <span title={new Date(invitation.createdAt).toLocaleString()}>
                Created:{" "}
                {new Date(invitation.createdAt).toLocaleDateString("de-CH")}
              </span>

              <span className={isExpired ? "text-red-500 font-semibold" : ""}>
                {invitation.isMultiUse
                  ? "Valid indefinitely (until regenerated)"
                  : isExpired
                    ? "Expired"
                    : `Expires in ${daysUntilExpiry} days`}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full lg:w-auto lg:flex-shrink-0">
          <div className="flex-1 lg:w-64 relative">
            <button
              type="button"
              onClick={() => setRevealed(!revealed)}
              className="w-full bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-600 font-mono cursor-pointer hover:bg-gray-100 transition-colors flex items-center justify-between select-none"
              title="Click to reveal or hide"
            >
              <span className="truncate mr-2 text-left">
                {revealed
                  ? invitation.invitationUrl
                  : "••••••••••••••••••••••••••••••••"}
              </span>
              <i
                className={`fas ${
                  revealed ? "fa-eye-slash" : "fa-eye"
                } text-gray-400 flex-shrink-0`}
              ></i>
            </button>
          </div>

          <button
            onClick={handleCopy}
            className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all flex-shrink-0 ${
              copied
                ? "bg-green-100 text-green-600"
                : "bg-white border border-gray-200 text-gray-500 hover:border-sunrise-red hover:text-sunrise-red"
            }`}
            title="Copy link"
          >
            <i className={`fas ${copied ? "fa-check" : "fa-copy"}`}></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default function InvitationLinksModal({ isOpen, onClose }) {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;
    const fetchInvitations = async () => {
      setLoading(true);
      try {
        const result = await authService.getActiveInvitations();
        if (!cancelled) {
          setInvitations(result.success ? result.invitations || [] : []);
        }
      } catch (error) {
        console.error("Failed to load invitations", error);
        if (!cancelled) setInvitations([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchInvitations();
    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Invitation Links"
      subtitle="Active invitation links for Berufsbildner, Practice Instructors and Revisors"
      icon="fa-link"
      size="xl"
    >
      {loading ? (
        <div className="flex flex-col items-center justify-center min-h-[160px] text-gray-400 text-center">
          <i className="fas fa-circle-notch fa-spin text-2xl mb-3 text-sunrise-red"></i>
          <p className="text-sm">Loading invitations...</p>
        </div>
      ) : invitations.length > 0 ? (
        <div className="space-y-3">
          {invitations.map((invitation) => (
            <InvitationLinkItem key={invitation.token} invitation={invitation} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center min-h-[160px] text-center py-6">
          <div className="w-14 h-14 bg-ice-white border border-pale-dawn rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-inbox text-gray-300 text-xl"></i>
          </div>
          <h3 className="text-base font-bold text-grey-dusk mb-1">
            No active invitations
          </h3>
          <p className="text-gray-500 text-sm">
            Generate invitations from the Invitations page.
          </p>
        </div>
      )}
    </Modal>
  );
}
